'use client';

import {useCallback, useEffect, useState} from 'react';
import {useRouter} from 'next/navigation';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from 'sonner';
import {login as loginApi, logout as logoutApi, getMe, getCapabilities, bootstrapAuth} from '@/lib/api/auth.api';
import {clearStoredToken, getStoredToken} from '@/lib/api/client';
import {useAuthStore} from '@/stores/authStore';
import {useMerchantStore} from '@/stores/merchantStore';
import type {LoginPayload} from '@/types/auth';
import type {AxiosError} from 'axios';

interface ApiErrorData {
    message?: string;
}

export function useAuth() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const {setUser, setCapabilities, setSubscription, clearAuth, isAuthenticated, user} = useAuthStore();

    // ── Hydration-safe: defer localStorage read to after mount ──
    // Server + client first-paint both see hydrated=false → identical HTML, no mismatch.
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => {
        setHydrated(true);
    }, []);

    const hasToken = hydrated && !!getStoredToken();

    // ── /me query ───────────────────────────────────────────────
    // queryFn is PURE — zero side-effects. Zustand sync happens in the
    // useEffect below so it runs AFTER render, not mid-query-resolution.
    const meQuery = useQuery({
        queryKey: ['me'],
        queryFn: async () => {
            const [meRes, capsRes] = await Promise.all([getMe(), getCapabilities()]);
            return {
                user: meRes.data?.user ?? null,
                capabilities: capsRes.data?.capabilities ?? null,
                subscription: capsRes.data?.subscription ?? null,
            };
        },
        enabled: hasToken,
        staleTime: 30_000,
        retry: false,
    });

    // Sync TanStack data → Zustand after render (safe, no mid-render side-effect)
    const queryData = meQuery.data ?? null;
    const queryUser = queryData?.user ?? null;
    useEffect(() => {
        if (queryUser) setUser(queryUser);
        if (queryData?.capabilities) setCapabilities(queryData.capabilities);
        if (queryData) setSubscription(queryData.subscription ?? null);
    }, [queryUser, queryData, setUser, setCapabilities, setSubscription]);

    // Resolved user: Zustand first (instant after login), then query data (after refresh)
    const resolvedUser = user ?? queryUser ?? null;

    // ── isLoading formula ───────────────────────────────────────
    // KEY: use meQuery.isPending (status === 'pending'), NOT meQuery.isLoading.
    //
    // meQuery.isLoading = isPending && isFetching
    // On the render where enabled flips true→false, TQ has not yet started
    // the network call: isPending=true but isFetching=false → isLoading=false.
    // That creates a 1-frame window where isLoading=false AND user=null, which
    // triggers the AdminLayout redirect before /me even fires.
    //
    // isPending=true covers that gap: it is true from the moment enabled flips
    // until the query settles (success OR error), regardless of isFetching.
    const isLoading =
        !hydrated ||                            // not mounted yet (SSR-safe)
        (hasToken && meQuery.isPending);        // mounted, token exists, /me not settled

    // ── Login ───────────────────────────────────────────────────
    const loginMutation = useMutation({
        mutationFn: (payload: LoginPayload) => loginApi(payload),
        onSuccess: async (res) => {
            const {token} = res.data;

            // bootstrapAuth: persists token → fetches /me + /capabilities + /businesses → hydrates Zustand
            const user = await bootstrapAuth(token);

            toast.success(res.message);
            queryClient.invalidateQueries({queryKey: ['me']});

            // Route based on platform role.
            // Platform roles: super_admin, admin, merchant.
            // merchant_staff is NOT a platform role — staff log in via /portal/:slug/staff/login
            const rolesList: string[] = user?.roles ?? [];
            const primaryRole: string = user?.role ?? '';
            const isSuperAdmin = primaryRole === 'super_admin' || rolesList.includes('super_admin');
            const isAdmin = primaryRole === 'admin' || rolesList.includes('admin');
            const isMerchant = primaryRole === 'merchant' || rolesList.includes('merchant');

            if (isSuperAdmin || isAdmin) {
                router.replace('/dashboard');
            } else if (isMerchant) {
                // Check if business selection is needed
                const { businesses, selectedBusinessId } = useMerchantStore.getState();
                if (businesses.length > 1 && !selectedBusinessId) {
                    router.replace('/merchant/select-business');
                } else {
                    router.replace('/merchant/dashboard');
                }
            } else {
                // Unknown role — clear auth and show error
                clearStoredToken();
                toast.error('Your account does not have portal access. Staff should use the staff login link.');
            }
        },
        onError: (error: AxiosError<ApiErrorData>) => {
            toast.error(error.response?.data?.message ?? 'Login failed');
        },
    });

    // ── Logout ──────────────────────────────────────────────────
    const logoutMutation = useMutation({
        mutationFn: logoutApi,
        onSuccess: (res) => {
            clearStoredToken();
            clearAuth();
            useMerchantStore.getState().clearBusiness();
            queryClient.clear();
            toast.success(res.message);
            router.push('/login');
        },
        onError: () => {
            clearStoredToken();
            clearAuth();
            useMerchantStore.getState().clearBusiness();
            queryClient.clear();
            router.push('/login');
        },
    });

    const handleLogout = useCallback(() => {
        logoutMutation.mutate();
    }, [logoutMutation]);

    return {
        user: resolvedUser,
        isAuthenticated,
        isLoading,
        login: loginMutation.mutate,
        loginPending: loginMutation.isPending,
        logout: handleLogout,
        logoutPending: logoutMutation.isPending,
    };
}
